import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, Bug, Copy, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { generateReportText, formatElementContextPreview, REPORT_WRITER_SYSTEM_PROMPT } from '@/utils/reportAIUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Markdown from 'markdown-to-jsx';
import type { ElementContextData } from '@/types/savedReports';

interface WriteWithAIModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (text: string) => void;
  availableElements: ElementContextData[];
  existingText?: string; // Existing text content to rewrite
}

export const WriteWithAIModal: React.FC<WriteWithAIModalProps> = ({
  open,
  onOpenChange,
  onGenerate,
  availableElements,
  existingText,
}) => {
  const [prompt, setPrompt] = useState('');
  const [selectedElementId, setSelectedElementId] = useState<string>('none');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const hasExistingText = !!existingText?.trim();

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setPrompt('');
      setSelectedElementId('none');
      setGeneratedText(null);
      setError(null);
    }
  }, [open]);

  const selectedElement = availableElements.find(el => el.elementId === selectedElementId);

  // Build the full prompt preview for debugging
  const buildFullPromptPreview = () => {
    let fullUserPrompt = `User instructions: ${prompt}`;
    if (existingText?.trim()) {
      fullUserPrompt += `\n\n---\nExisting text to rewrite:\n${existingText}`;
    }
    if (selectedElement) {
      fullUserPrompt += `\n\n---\nContext from "${selectedElement.label}":\n${selectedElement.contextText}`;
    }
    return `=== SYSTEM PROMPT ===\n${REPORT_WRITER_SYSTEM_PROMPT}\n\n=== USER PROMPT ===\n${fullUserPrompt}`;
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(buildFullPromptPreview());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    setError(null);
    setGeneratedText(null);

    try {
      // Build prompt with existing text if available
      let fullPrompt = prompt;
      if (existingText?.trim()) {
        fullPrompt = `${prompt}\n\n---\nExisting text to rewrite:\n${existingText}`;
      }
      const text = await generateReportText(fullPrompt, selectedElement);
      setGeneratedText(text);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate text');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleConfirm = () => {
    if (generatedText) {
      onGenerate(generatedText);
      onOpenChange(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey && !isGenerating && prompt.trim()) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Write with AI
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Prompt Input */}
          <div className="space-y-2">
            <Label htmlFor="ai-prompt">What would you like to write?</Label>
            <Textarea
              id="ai-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., Write a summary of the key trends shown in the data..."
              rows={3}
              autoFocus
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">Press Cmd+Enter to generate</p>
              <div className="flex items-center gap-3">
                {hasExistingText && (
                  <button
                    type="button"
                    onClick={() => setPrompt('Rewrite the existing text to be more concise and polished while preserving the key points and meaning.')}
                    className="text-xs text-purple-600 hover:text-purple-700 hover:underline"
                  >
                    Rewrite
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setPrompt('Summarize key trends in the provided data, focusing on notable patterns across categories. Describe relative changes and comparisons rather than absolute numbers.')}
                  className="text-xs text-purple-600 hover:text-purple-700 hover:underline"
                >
                  Summarize Data
                </button>
              </div>
            </div>
          </div>

          {/* Context Selection */}
          <div className="space-y-2">
            <Label>Include context from (optional)</Label>
            <Select value={selectedElementId} onValueChange={setSelectedElementId}>
              <SelectTrigger>
                <SelectValue placeholder="Select an element for context" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No context</SelectItem>
                {availableElements.map((element) => (
                  <SelectItem key={element.elementId} value={element.elementId}>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{element.label}</span>
                      <span className="text-xs text-gray-500 truncate max-w-[250px]">
                        {element.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Existing Text Preview */}
          {hasExistingText && (
            <div className="space-y-2">
              <Label className="text-gray-500">Existing text:</Label>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-600 max-h-24 overflow-y-auto">
                <p className="whitespace-pre-wrap line-clamp-4">{existingText}</p>
              </div>
            </div>
          )}

          {/* Generated Text Preview */}
          {generatedText && (
            <div className="space-y-2">
              <Label>Generated text:</Label>
              <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm prose prose-sm max-w-none">
                <Markdown>{generatedText}</Markdown>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleCopyPrompt}
                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                >
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Bug className="h-4 w-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" align="start" className="max-w-md max-h-80 overflow-auto">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">Full Prompt Preview</span>
                    <button
                      onClick={handleCopyPrompt}
                      className="text-xs flex items-center gap-1 hover:text-blue-600"
                    >
                      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="text-xs whitespace-pre-wrap font-mono bg-gray-50 p-2 rounded">
                    {buildFullPromptPreview()}
                  </pre>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isGenerating}>
              Cancel
            </Button>
            {generatedText ? (
              <Button onClick={handleConfirm}>
                Use This Text
              </Button>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={!prompt.trim() || isGenerating}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
